import React, { useState, useEffect } from 'react';
// @ts-ignore - lucide-react types incompatible with TS 4.9.5
import { MapPin, Route, Truck, TrendingUp, ShieldCheck, AlertTriangle, Package, Factory } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Line, PieChart, Pie, Cell, Legend, Area, ComposedChart
} from 'recharts';
import { clientDashboardService } from '../../services/clientDashboard';

interface Props {
  filters: { periodo: string; dataInicio?: string; dataFim?: string };
  refreshKey: number;
}

const COLORS = ['#3b82f6', '#22c55e', '#eab308', '#ef4444', '#a855f7', '#06b6d4', '#f97316', '#ec4899'];
const DIAS_SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

function formatSeconds(s: number): string {
  if (!s || s <= 0) return '-';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.round(s % 60);
  if (h > 0) return `${h}h${m.toString().padStart(2, '0')}m`;
  if (m > 0) return `${m}m${sec.toString().padStart(2, '0')}s`;
  return `${sec}s`;
}

function formatCurrency(v: number): string {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatCNPJ(cnpj: string): string {
  if (!cnpj) return '-';
  if (cnpj.length === 14) return cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  if (cnpj.length === 11) return cnpj.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  return cnpj;
}

const ClientDashboardAnalytics: React.FC<Props> = ({ filters, refreshKey }) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');
        const res = await clientDashboardService.getAnalytics(filters);
        setData(res.data);
      } catch (err: any) {
        if (err.response?.status !== 401) {
          setError('Erro ao carregar analytics. Tente novamente.');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [filters.periodo, filters.dataInicio, filters.dataFim, refreshKey]);

  if (loading) {
    return (
      <div className="client-dashboard-loading">
        <div className="client-dashboard-spinner" />
        Carregando analytics...
      </div>
    );
  }

  if (error) {
    return <div className="client-dashboard-error-message">{error}</div>;
  }

  if (!data) return null;

  const sla = data.sla || {};
  const heatmapData = buildHeatmapData(data.heatmap || []);

  return (
    <>
      {/* SLA e Indicadores de Qualidade */}
      <h2 className="client-dashboard-section-title">Indicadores de Qualidade e SLA</h2>
      <div className="client-dashboard-cards">
        <div className="client-dashboard-card green">
          <div className="client-dashboard-card-icon"><ShieldCheck size={20} /></div>
          <div className="client-dashboard-card-value">{sla.taxa_conclusao || 0}%</div>
          <div className="client-dashboard-card-label">Taxa de Conclusão</div>
        </div>
        <div className="client-dashboard-card blue">
          <div className="client-dashboard-card-icon"><TrendingUp size={20} /></div>
          <div className="client-dashboard-card-value">{sla.taxa_perfeicao || 0}%</div>
          <div className="client-dashboard-card-label">Taxa de Perfeição (sem divergência)</div>
        </div>
        <div className="client-dashboard-card red">
          <div className="client-dashboard-card-icon"><AlertTriangle size={20} /></div>
          <div className="client-dashboard-card-value">{sla.taxa_divergencia || 0}%</div>
          <div className="client-dashboard-card-label">Taxa de Divergência</div>
        </div>
        <div className="client-dashboard-card cyan">
          <div className="client-dashboard-card-icon"><Package size={20} /></div>
          <div className="client-dashboard-card-value">{(sla.total || 0).toLocaleString('pt-BR')}</div>
          <div className="client-dashboard-card-label">Total de Conferências</div>
        </div>
      </div>

      {/* Funil de Conferências */}
      <div className="client-dashboard-charts">
        <div className="client-dashboard-chart-container">
          <h3 className="client-dashboard-chart-title">Funil de Conferências</h3>
          <div className="client-analytics-funnel">
            <FunnelBar label="Total" value={sla.total} max={sla.total} color="#3b82f6" />
            <FunnelBar label="Concluídas" value={sla.concluidas} max={sla.total} color="#22c55e" />
            <FunnelBar label="Divergentes" value={sla.divergentes} max={sla.total} color="#ef4444" />
            <FunnelBar label="Canceladas" value={sla.canceladas} max={sla.total} color="#94a3b8" />
            <FunnelBar label="Em Aberto" value={sla.em_aberto} max={sla.total} color="#eab308" />
          </div>
        </div>

        <div className="client-dashboard-chart-container">
          <h3 className="client-dashboard-chart-title">Distribuição por Tipo</h3>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={(data.tipos_conferencia || []).map((t: any) => ({
                  name: t.tipo === 'pre_carregamento' ? 'Pré-Carreg.' :
                        t.tipo === 'carregamento' ? 'Carregamento' : 'Descarreg.',
                  value: t.total,
                  itens: t.total_itens,
                  divergencias: t.total_divergencias
                }))}
                dataKey="value"
                nameKey="name"
                cx="50%" cy="50%"
                outerRadius={100}
                label={({ name, percent }: any) => `${name} (${(percent * 100).toFixed(0)}%)`}
              >
                {(data.tipos_conferencia || []).map((_: any, i: number) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value: any, name: any, entry: any) => [
                `${value} conf. | ${entry.payload.itens || 0} itens | ${entry.payload.divergencias || 0} diverg.`,
                name
              ]} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Evolução Diária */}
      <div className="client-dashboard-chart-container" style={{ marginBottom: 24 }}>
        <h3 className="client-dashboard-chart-title">Evolução Diária - Conferências vs Divergências</h3>
        <ResponsiveContainer width="100%" height={320}>
          <ComposedChart data={(data.evolucao_diaria || []).map((d: any) => ({
            dia: new Date(d.dia).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
            conferencias: d.total_conferencias,
            itens: d.total_itens,
            divergencias: d.total_divergencias,
            tempo_medio: Math.round((d.tempo_medio || 0) / 60)
          }))}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.1)" />
            <XAxis dataKey="dia" stroke="#94a3b8" />
            <YAxis yAxisId="left" stroke="#94a3b8" />
            <YAxis yAxisId="right" orientation="right" stroke="#ef4444" />
            <Tooltip contentStyle={{
              background: '#1e293b', border: '1px solid rgba(148,163,184,0.2)',
              borderRadius: '8px', color: '#e2e8f0'
            }} />
            <Area yAxisId="left" type="monotone" dataKey="itens" fill="rgba(6,182,212,0.1)" stroke="#06b6d4" name="Itens Conferidos" />
            <Bar yAxisId="left" dataKey="conferencias" fill="#3b82f6" radius={[4, 4, 0, 0]} name="Conferências" />
            <Line yAxisId="right" type="monotone" dataKey="divergencias" stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} name="Divergências" />
            <Legend />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Áreas de Entrega por Volume */}
      <h2 className="client-dashboard-section-title">
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <MapPin size={18} /> Análise por Área de Entrega
        </span>
      </h2>
      {(data.areas_entrega || []).length > 0 ? (
        <div className="client-dashboard-table-container">
          <table className="client-dashboard-table">
            <thead>
              <tr>
                <th>Código</th>
                <th>Nome da Área</th>
                <th>Região</th>
                <th>Pedidos</th>
                <th>Etiquetas</th>
                <th>Divergentes</th>
                <th>Taxa Diverg.</th>
                <th>Valor Total</th>
                <th>Peso (kg)</th>
              </tr>
            </thead>
            <tbody>
              {data.areas_entrega.map((d: any, i: number) => {
                const taxaDiv = d.total_etiquetas > 0 ? ((d.total_divergentes / d.total_etiquetas) * 100).toFixed(1) : '0';
                return (
                  <tr key={i}>
                    <td style={{ fontWeight: 600, fontFamily: 'monospace' }}>{d.area_codigo || '-'}</td>
                    <td>{d.area_nome || '-'}</td>
                    <td>{d.area_regiao || '-'}</td>
                    <td>{d.total_pedidos.toLocaleString('pt-BR')}</td>
                    <td>{d.total_etiquetas.toLocaleString('pt-BR')}</td>
                    <td>
                      <span className={`client-dashboard-badge ${d.total_divergentes > 0 ? 'divergente' : 'concluido'}`}>
                        {d.total_divergentes}
                      </span>
                    </td>
                    <td>
                      <span style={{ color: parseFloat(taxaDiv) > 5 ? '#f87171' : parseFloat(taxaDiv) > 2 ? '#facc15' : '#4ade80' }}>
                        {taxaDiv}%
                      </span>
                    </td>
                    <td>{d.valor_total > 0 ? formatCurrency(d.valor_total) : '-'}</td>
                    <td>{d.peso_total > 0 ? d.peso_total.toLocaleString('pt-BR', { maximumFractionDigits: 1 }) : '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="client-dashboard-config-card" style={{ textAlign: 'center', color: '#64748b' }}>
          Sem dados de áreas de entrega no período selecionado
        </div>
      )}

      {/* Áreas de Entrega e Paletes por Área */}
      <div className="client-dashboard-charts">
        <div className="client-dashboard-chart-container">
          <h3 className="client-dashboard-chart-title">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <Route size={16} /> Performance por Área de Entrega
            </span>
          </h3>
          {(data.conferencias_por_area || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data.conferencias_por_area.map((r: any) => {
                const label = r.area_codigo && r.area_nome ? `${r.area_codigo} (${r.area_nome})` : (r.area_codigo || r.area_nome || '-');
                return {
                  area: label.length > 25 ? label.substring(0, 25) + '...' : label,
                  conferencias: r.total_conferencias,
                  divergencias: r.total_divergencias,
                  tempo: Math.round((r.tempo_medio || 0) / 60)
                };
              })} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.1)" />
                <XAxis type="number" stroke="#94a3b8" />
                <YAxis type="category" dataKey="area" stroke="#94a3b8" width={160} tick={{ fontSize: 12 }} />
                <Tooltip contentStyle={{
                  background: '#1e293b', border: '1px solid rgba(148,163,184,0.2)',
                  borderRadius: '8px', color: '#e2e8f0'
                }} />
                <Bar dataKey="conferencias" fill="#3b82f6" radius={[0, 4, 4, 0]} name="Conferências" />
                <Bar dataKey="divergencias" fill="#ef4444" radius={[0, 4, 4, 0]} name="Divergências" />
                <Legend />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>Sem dados de áreas de entrega</div>
          )}
        </div>

        <div className="client-dashboard-chart-container">
          <h3 className="client-dashboard-chart-title">Paletes por Área de Entrega</h3>
          {(data.paletes_por_area || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data.paletes_por_area.map((r: any) => {
                const label = r.area_codigo && r.area_nome ? `${r.area_codigo} (${r.area_nome})` : (r.area_codigo || r.area_nome || '-');
                return {
                  area: label.length > 25 ? label.substring(0, 25) + '...' : label,
                  paletes: r.total_paletes,
                  ok: r.leituras_ok,
                  duplicadas: r.leituras_duplicadas
                };
              })} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.1)" />
                <XAxis type="number" stroke="#94a3b8" />
                <YAxis type="category" dataKey="area" stroke="#94a3b8" width={160} tick={{ fontSize: 12 }} />
                <Tooltip contentStyle={{
                  background: '#1e293b', border: '1px solid rgba(148,163,184,0.2)',
                  borderRadius: '8px', color: '#e2e8f0'
                }} />
                <Bar dataKey="paletes" fill="#a855f7" radius={[0, 4, 4, 0]} name="Paletes" />
                <Legend />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>Sem dados de paletes</div>
          )}
        </div>
      </div>

      {/* Veículos e Destinatários */}
      <div className="client-dashboard-charts">
        <div className="client-dashboard-chart-container">
          <h3 className="client-dashboard-chart-title">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <Truck size={16} /> Performance por Veículo
            </span>
          </h3>
          {(data.veiculos || []).length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table className="client-dashboard-table">
                <thead>
                  <tr>
                    <th>Placa</th>
                    <th>Conf.</th>
                    <th>Itens</th>
                    <th>Diverg.</th>
                    <th>Tempo Méd.</th>
                  </tr>
                </thead>
                <tbody>
                  {data.veiculos.map((v: any, i: number) => (
                    <tr key={i}>
                      <td style={{ fontWeight: 600 }}>{v.placa}</td>
                      <td>{v.total_conferencias}</td>
                      <td>{(v.total_itens || 0).toLocaleString('pt-BR')}</td>
                      <td>
                        <span className={`client-dashboard-badge ${v.total_divergencias > 0 ? 'divergente' : 'concluido'}`}>
                          {v.total_divergencias}
                        </span>
                      </td>
                      <td>{formatSeconds(v.tempo_medio)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>Sem dados de veículos</div>
          )}
        </div>

        <div className="client-dashboard-chart-container">
          <h3 className="client-dashboard-chart-title">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <Factory size={16} /> Top Destinatários
            </span>
          </h3>
          {(data.destinatarios || []).length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table className="client-dashboard-table">
                <thead>
                  <tr>
                    <th>CNPJ</th>
                    <th>Destinatário</th>
                    <th>Pedidos</th>
                    <th>Etiq.</th>
                    <th>Diverg.</th>
                    <th>Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {data.destinatarios.map((d: any, i: number) => (
                    <tr key={i}>
                      <td style={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '0.85em' }}>
                        {formatCNPJ(d.destinatario_cnpj)}
                      </td>
                      <td style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          title={d.destinatario_nome}>
                        {d.destinatario_nome || '-'}
                      </td>
                      <td>{d.total_pedidos}</td>
                      <td>{d.total_etiquetas}</td>
                      <td>
                        <span className={`client-dashboard-badge ${d.total_divergentes > 0 ? 'divergente' : 'concluido'}`}>
                          {d.total_divergentes}
                        </span>
                      </td>
                      <td>{d.valor_total > 0 ? formatCurrency(d.valor_total) : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>Sem dados de destinatários</div>
          )}
        </div>
      </div>

      {/* Heatmap de Operação */}
      <div className="client-dashboard-chart-container" style={{ marginBottom: 24 }}>
        <h3 className="client-dashboard-chart-title">Mapa de Calor - Volume de Operação (Hora x Dia da Semana)</h3>
        <div className="client-analytics-heatmap">
          <div className="client-analytics-heatmap-header">
            <div className="client-analytics-heatmap-label" />
            {Array.from({ length: 24 }, (_, h) => (
              <div key={h} className="client-analytics-heatmap-hour">{h}h</div>
            ))}
          </div>
          {DIAS_SEMANA.map((dia, dIdx) => (
            <div key={dIdx} className="client-analytics-heatmap-row">
              <div className="client-analytics-heatmap-label">{dia}</div>
              {Array.from({ length: 24 }, (_, h) => {
                const cell = heatmapData[dIdx]?.[h] || 0;
                const maxVal = Math.max(...Object.values(heatmapData).flatMap((d: any) => Object.values(d) as number[]), 1);
                const intensity = cell / maxVal;
                return (
                  <div
                    key={h}
                    className="client-analytics-heatmap-cell"
                    style={{
                      background: cell > 0
                        ? `rgba(59, 130, 246, ${0.15 + intensity * 0.85})`
                        : 'rgba(148, 163, 184, 0.05)'
                    }}
                    title={`${dia} ${h}h: ${cell} leituras`}
                  >
                    {cell > 0 && intensity > 0.5 ? cell : ''}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Ocorrências Detalhadas */}
      {(data.ocorrencias_detalhadas || []).length > 0 && (
        <div className="client-dashboard-chart-container" style={{ marginBottom: 24 }}>
          <h3 className="client-dashboard-chart-title">Ocorrências por Tipo</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.ocorrencias_detalhadas.map((o: any) => ({
              tipo: o.tipo.length > 25 ? o.tipo.substring(0, 25) + '...' : o.tipo,
              total: o.total,
              quantidade: o.quantidade_divergente
            }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.1)" />
              <XAxis dataKey="tipo" stroke="#94a3b8" tick={{ fontSize: 11 }} />
              <YAxis stroke="#94a3b8" />
              <Tooltip contentStyle={{
                background: '#1e293b', border: '1px solid rgba(148,163,184,0.2)',
                borderRadius: '8px', color: '#e2e8f0'
              }} />
              <Bar dataKey="total" fill="#3b82f6" name="Ocorrências" radius={[4, 4, 0, 0]} />
              <Bar dataKey="quantidade" fill="#ef4444" name="Qtd. Divergente" radius={[4, 4, 0, 0]} />
              <Legend />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Tabela de Áreas de Entrega Detalhada */}
      {(data.conferencias_por_area || []).length > 0 && (
        <div className="client-dashboard-table-container">
          <h3 className="client-dashboard-table-title">Detalhamento por Área de Entrega</h3>
          <table className="client-dashboard-table">
            <thead>
              <tr>
                <th>Código</th>
                <th>Nome da Área</th>
                <th>Região</th>
                <th>Conferências</th>
                <th>Itens</th>
                <th>Divergências</th>
                <th>Concluídas</th>
                <th>Tempo Médio</th>
                <th>Taxa Sucesso</th>
              </tr>
            </thead>
            <tbody>
              {data.conferencias_por_area.map((r: any, i: number) => {
                const taxa = r.total_conferencias > 0
                  ? ((r.concluidas / r.total_conferencias) * 100).toFixed(0)
                  : '0';
                return (
                  <tr key={i}>
                    <td style={{ fontWeight: 600, fontFamily: 'monospace' }}>{r.area_codigo || '-'}</td>
                    <td>{r.area_nome || '-'}</td>
                    <td>{r.area_regiao || '-'}</td>
                    <td>{r.total_conferencias}</td>
                    <td>{(r.total_itens || 0).toLocaleString('pt-BR')}</td>
                    <td>
                      <span className={`client-dashboard-badge ${r.total_divergencias > 0 ? 'divergente' : 'concluido'}`}>
                        {r.total_divergencias}
                      </span>
                    </td>
                    <td>{r.concluidas}</td>
                    <td>{formatSeconds(r.tempo_medio)}</td>
                    <td>
                      <span style={{ color: parseInt(taxa) >= 90 ? '#4ade80' : parseInt(taxa) >= 70 ? '#facc15' : '#f87171' }}>
                        {taxa}%
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

// Componente de barra do funil
const FunnelBar: React.FC<{ label: string; value: number; max: number; color: string }> = ({ label, value, max, color }) => {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="client-analytics-funnel-item">
      <span className="client-analytics-funnel-label">{label}</span>
      <div className="client-analytics-funnel-track">
        <div
          className="client-analytics-funnel-fill"
          style={{ width: `${Math.max(pct, value > 0 ? 3 : 0)}%`, background: color }}
        />
      </div>
      <span className="client-analytics-funnel-value">{(value || 0).toLocaleString('pt-BR')}</span>
    </div>
  );
};

// Helper para construir dados do heatmap
function buildHeatmapData(raw: any[]): Record<number, Record<number, number>> {
  const map: Record<number, Record<number, number>> = {};
  for (let d = 0; d < 7; d++) {
    map[d] = {};
    for (let h = 0; h < 24; h++) map[d][h] = 0;
  }
  raw.forEach(r => {
    map[r.dia_semana] = map[r.dia_semana] || {};
    map[r.dia_semana][r.hora] = r.total;
  });
  return map;
}

export default ClientDashboardAnalytics;
